import { appendVueRoute, PayedFeatureType, Role, type RoutePath } from '@therame/shared';
import { type RouteRecordRaw } from 'vue-router';
import { type RestrictedRecordRaw } from '..';

const routes: Array<RouteRecordRaw> = [
  {
    ...appendVueRoute<'exerciseLibrary'>('exerciseLibrary', '/exercises/library'),
    // Workspace catch-all: serializes the open cascade into the URL (PRD #963).
    // Empty cascade is the base path; deeper paths like
    // `/exercises/library/exerciseDetails:abc` are columns.
    path: '/exercises/library/:cascade(.*)?' as RoutePath<'exerciseLibrary'>,
    components: {
      default: () => import('@views/ExerciseLibrary.vue'),
      leftSidebar: () => import('@components/ExerciseLibrary/LibrarySideList/LibrarySideList.vue'),
    },
    meta: {
      layout: 'default',
      noPaddingBottom: true,
      title: 'Übungsbibliothek',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'exerciseUploadLibrary'>(
      'exerciseUploadLibrary',
      '/exercise-upload/l/:visibility/:token',
    ),
    component: () => import('@src/views/ExerciseUpload.vue'),
    meta: {
      layout: 'none',
      title: 'Übung hochladen',
      hideTitle: false,
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'exerciseUploadLibraryAuth'>(
      'exerciseUploadLibraryAuth',
      '/exercise-upload/la/:visibility/:token',
    ),
    // Same view as its unauthenticated twin; `fromQrCodeAuth` is the only difference,
    // and it tells the panel to pick the half-built Übung back up out of localStorage
    // after the keycloak redirect (ADR-0029 §H-4).
    component: () => import('@src/views/ExerciseUpload.vue'),
    props: { fromQrCodeAuth: true },
    meta: {
      layout: 'none',
      title: 'Übung hochladen',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseUploadPatientAuth'>(
      'exerciseUploadPatientAuth',
      '/exercise-upload/pa/:visibility/:token/:userId/:userName',
    ),
    // Same view as its unauthenticated twin; `fromQrCodeAuth` is the only difference,
    // and it tells the panel to pick the half-built Übung back up out of localStorage
    // after the keycloak redirect (ADR-0029 §H-4).
    component: () => import('@src/views/ExerciseUpload.vue'),
    props: { fromQrCodeAuth: true },
    meta: {
      layout: 'none',
      title: 'Übung hochladen',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseUploadPatient'>(
      'exerciseUploadPatient',
      '/exercise-upload/p/:visibility/:token/:userId/:userName',
    ),
    component: () => import('@src/views/ExerciseUpload.vue'),
    meta: {
      layout: 'none',
      title: 'Übung hochladen',
      hideTitle: false,
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'exerciseLibraryWithPatient'>(
      'exerciseLibraryWithPatient',
      '/exercises/:view/with-patient/:patientId/:patientName',
    ),
    component: () => import('@src/views/ExerciseLibrary.vue'),
    meta: {
      layout: 'default',
      noPaddingBottom: true,
      title: 'Übungsbibliothek',
      hideTitle: true,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseExecution'>('exerciseExecution', '/exercise-execution/overview'),
    component: () => import('@src/views/ExerciseExecution/ExerciseExecution.vue'),
    meta: {
      layout: 'default',
      title: 'Übungsprogramm',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseExecutionPlayer'>(
      'exerciseExecutionPlayer',
      '/exercise-execution/:exerciseProgramId/player',
    ),
    component: () => import('@src/views/ExerciseExecution/ExerciseExecutionPlayer.vue'),
    meta: {
      layout: 'default',
      title: 'Übungsdurchführung',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      hideNav: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseExecutionSuccess'>(
      'exerciseExecutionSuccess',
      '/exercise-execution/:exerciseProgramId/success',
    ),
    component: () => import('@src/views/ExerciseExecution/ExerciseExecutionSuccess.vue'),
    meta: {
      layout: 'default',
      title: 'Übungsdurchführung',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      hideNav: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'exerciseExecutionEvalForm'>(
      'exerciseExecutionEvalForm',
      '/exercise-execution/:exerciseProgramId/eval-form',
    ),
    component: () => import('@src/views/ExerciseExecution/ExerciseExecutionEvalForm.vue'),
    meta: {
      layout: 'default',
      title: 'Übungsfeedback',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      hideNav: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
] satisfies RestrictedRecordRaw[];

export default routes;
